import React, { useState } from 'react'
import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { Pagination } from '@e3dc-react/shell/Elements/Table'
import { J2Multiply, J2Validate } from '@e3dc-react/icons'
import { DeviceIcon } from 'src/Libs/flow/icons/DeviceIcon'
import { PlannedInstallationComponent } from 'src/Libs/installations/hooks/installation-configuration/use-planned-installation-devices'
import { getPlannedDeviceStatus, PlannedDeviceStatus } from 'src/Libs/installations/utils/get-planned-device-status'

//#region DevicesTable

type DevicesTableProps = {
  devices: PlannedInstallationComponent[]
  isEmcOnline: boolean
}

const STATUS_TRANSLATION_KEYS: Record<PlannedDeviceStatus, string> = {
  notConnected: 'installation-configuration.component-list.status.notConnected',
  notPaired: 'installation-configuration.component-list.status.notPaired',
  online: 'common.online',
  offline: 'common.offline',
}

const DEFAULT_MOBILE_PAGE_SIZE = 10

export const DevicesTable: React.FC<DevicesTableProps> = ({ devices, isEmcOnline }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [mobilePage, setMobilePage] = useState(0)
  const [mobileLimit, setMobileLimit] = useState(DEFAULT_MOBILE_PAGE_SIZE)
  const emc = devices.find(device => device.deviceCategory === 'EMC')
  const currentMobilePage = Math.min(mobilePage, Math.max(0, Math.ceil(devices.length / mobileLimit) - 1))

  const getDeviceName = (device: PlannedInstallationComponent): string => {
    if (device.deviceCategory === 'EMC') return 'EM 360'
    if (device.deviceCategory === 'Storage') {
      return t('installation-configuration.component-list.storage-name')
    }
    return device.name
  }

  if (isSm) {
    return (
      <Box>
        <Typography fontWeight="bold" sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider' }}>
          {t('installation-configuration.component-list.name', { defaultValue: 'Name' })} / {t('common.serialnumber')}
        </Typography>
        {devices.length === 0 ? (
          <Typography py={2} textAlign="center" color="textDisabled">
            {t('shell.noData')}
          </Typography>
        ) : (
          devices
            .slice(currentMobilePage * mobileLimit, (currentMobilePage + 1) * mobileLimit)
            .map((device, index) => (
              <DeviceAccordion
                key={device.id}
                device={device}
                name={getDeviceName(device)}
                serial={device.serialNumber ?? '-'}
                status={getPlannedDeviceStatus(device, emc, isEmcOnline)}
                striped={index % 2 !== 0}
              />
            ))
        )}
        {devices.length > DEFAULT_MOBILE_PAGE_SIZE && (
          <Pagination
            count={devices.length}
            page={currentMobilePage}
            limit={mobileLimit}
            setLimit={setMobileLimit}
            setPage={setMobilePage}
            isMobilePaginator
            elevation={0}
          />
        )}
      </Box>
    )
  }

  return (
    <TableContainer>
      <Table size="small" sx={{ minWidth: 420 }}>
        <TableHead>
          <TableRow>
            <TableCell width="33%">{t('installation-configuration.component-list.name', { defaultValue: 'Name' })}</TableCell>
            <TableCell width="33%">{t('common.serialnumber')}</TableCell>
            <TableCell width="33%">{t('installation-configuration.component-list.status.title', { defaultValue: 'Status' })}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {devices.length === 0 ? (
            <TableRow sx={{ '&:last-child td': { border: 0 } }}>
              <TableCell colSpan={3} align="center">
                <Typography pt={2} color="textDisabled">
                  {t('shell.noData')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            devices.map(device => {
              const status = getPlannedDeviceStatus(device, emc, isEmcOnline)
              return (
                <TableRow key={device.id} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <DeviceIcon device={device.deviceCategory} sx={{ fontSize: 30 }} />
                      {getDeviceName(device)}
                    </Stack>
                  </TableCell>
                  <TableCell>{device.serialNumber ?? '-'}</TableCell>
                  <TableCell>{t(STATUS_TRANSLATION_KEYS[status])}</TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

//#endregion DevicesTable

//#region DeviceAccordion

type DeviceAccordionProps = {
  device: PlannedInstallationComponent
  name: string
  serial: string
  status: PlannedDeviceStatus
  striped: boolean
}

const DeviceAccordion: React.FC<DeviceAccordionProps> = ({ device, name, serial, status, striped }) => {
  const { t } = useCustomTranslation()
  const statusLabel = t(STATUS_TRANSLATION_KEYS[status])

  return (
    <AccordionItem
      elevation={0}
      customWidth="calc(100% - 32px)"
      title={
        <Stack direction="row" gap={2} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
          <DeviceIcon device={device.deviceCategory} sx={{ fontSize: '24px !important', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            <Typography>{name}</Typography>
            <Typography fontWeight="bold">{serial}</Typography>
          </Box>
        </Stack>
      }
      headerChildren={<DeviceConnectionIcon status={status} label={statusLabel} />}
      accordionDetailsSx={{ px: 2, py: 2 }}
      sx={{
        bgcolor: striped ? 'action.hover' : 'background.paper',
        border: 0,
        borderBottom: 1,
        borderColor: 'divider',
        borderRadius: '0 !important',
        '& .MuiAccordionSummary-root': { flexDirection: 'row-reverse', px: 2, gap: 2 },
        '& .MuiAccordionSummary-content': { flex: 1, width: '100%', minWidth: 0, mr: 0, py: 1 },
        '& .MuiAccordionSummary-expandIconWrapper svg': { fontSize: 20 },
      }}
    >
      <Stack direction="row" gap={2} alignItems="center">
        <Typography sx={{ overflowWrap: 'anywhere' }}>{statusLabel}</Typography>
        <DeviceConnectionIcon status={status} label={statusLabel} />
      </Stack>
    </AccordionItem>
  )
}

type DeviceConnectionIconProps = {
  status: PlannedDeviceStatus
  label: string
}

const DeviceConnectionIcon: React.FC<DeviceConnectionIconProps> = ({ status, label }) => {
  const iconProps = { titleAccess: label, sx: { fontSize: '24px !important', flexShrink: 0 } }
  return status === 'online' ? <J2Validate {...iconProps} color="success" /> : <J2Multiply {...iconProps} color="error" />
}

//#endregion DeviceAccordion
